import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Divider,
  Card,
  InputAdornment,
  List,
  ListItem,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import DeleteIcon from '@mui/icons-material/Delete'
import AddIcon from '@mui/icons-material/Add'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import VisibilityIcon from '@mui/icons-material/Visibility'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import { useState, useCallback } from 'react'
import { toast } from 'react-toastify'
import { sanitizeHtml } from '../../../../utility/utilityFunctions'
import { FIELD_TYPES } from '../../../../utility/constants'
import ConfirmationBox from '../../../../components/Dashboard/ConfirmationBox'

// ─── Helpers ──────────────────────────────────────────────────────────────────

const TabPanel = ({ children, value, index }) =>
  value === index ? <Box sx={{ pt: 2.5, pb: 1 }}>{children}</Box> : null

const SectionLabel = ({ children }) => (
  <Typography
    variant="overline"
    sx={{
      color: 'text.disabled',
      letterSpacing: 1.2,
      fontSize: '0.68rem',
      mb: 1,
      display: 'block',
    }}
  >
    {children}
  </Typography>
)

const PRESET_COLORS = [
  { value: '#ffffff', label: 'White' },
  { value: '#f8fafc', label: 'Slate 50' },
  { value: '#f1f5f9', label: 'Slate 100' },
  { value: '#f0f9ff', label: 'Sky 50' },
  { value: '#f0fdf4', label: 'Green 50' },
  { value: '#fffbeb', label: 'Amber 50' },
  { value: '#fff1f2', label: 'Rose 50' },
  { value: 'transparent', label: 'Transparent' },
]

// ─── Copy-to-clipboard helper ────────────────────────────────────────────────
const copyToClipboard = (text) => {
  navigator.clipboard
    .writeText(text)
    .then(() => {
      toast.success(`Copied ${text} to clipboard`, { autoClose: 1500 })
    })
    .catch(() => {
      toast.error('Failed to copy')
    })
}

// ─── PlaceholderChip ─────────────────────────────────────────────────────────
const PlaceholderChip = ({ tag, label }) => (
  <Tooltip title={`Click to copy ${tag}`} placement="top">
    <Chip
      icon={<ContentCopyIcon sx={{ fontSize: '0.75rem !important' }} />}
      label={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography
            component="span"
            sx={{
              fontFamily: 'monospace',
              fontSize: '0.72rem',
              fontWeight: 600,
              color: 'secondary.main',
            }}
          >
            {tag}
          </Typography>
          {label && (
            <Typography component="span" sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>
              {label}
            </Typography>
          )}
        </Box>
      }
      size="small"
      variant="outlined"
      onClick={() => copyToClipboard(tag)}
      sx={{
        cursor: 'pointer',
        borderColor: 'divider',
        height: 'auto',
        py: 0.25,
        '& .MuiChip-label': { px: 0.75 },
        '&:hover': { borderColor: 'secondary.main', bgcolor: 'action.hover' },
      }}
    />
  </Tooltip>
)

// ─── PlaceholderSidebar ───────────────────────────────────────────────────────
const PlaceholderSidebar = ({ activityFields, allTables }) => {
  // General fields = not a subfield (table_field_template_id is null) and not a table itself
  const generalFields = activityFields.filter(
    (f) => !f.table_field_template_id && f.data_structure !== FIELD_TYPES.TABLE,
  )

  // Table fields with their subfields already contained in allTables
  const tableFields = allTables

  return (
    <Box
      sx={{
        width: 300,
        flexShrink: 0,
        height: '500',
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1.5,
        overflow: 'auto',
        bgcolor: 'background.default',
      }}
    >
      {/* Sidebar header */}
      <Box
        sx={{
          px: 1.5,
          pt: 1.5,
          pb: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Typography
          variant="caption"
          sx={{
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: 1,
            color: 'text.disabled',
            display: 'block',
            mb: 0.75,
          }}
        >
          Available Placeholders
        </Typography>
      </Box>

      {/* Scrollable list */}
      <Box sx={{ flex: 1, overflowY: 'auto', p: 1, height: 'fit-content' }}>
        {/* General fields */}
        {generalFields.length > 0 && (
          <Box sx={{ mb: 1.5 }}>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: 0.8,
                color: 'text.disabled',
                display: 'block',
                mb: 0.5,
                px: 0.5,
              }}
            >
              General Fields
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {generalFields.map((f) => (
                <Box
                  key={f.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 0.5,
                    py: 0.25,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.primary',
                      flexShrink: 0,
                      maxWidth: '55%',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {f.field_template_name}
                  </Typography>
                  <PlaceholderChip tag={`[[f_${f.id}]]`} />
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* Table fields */}
        {tableFields.length > 0 && (
          <Box>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: 0.8,
                color: 'text.disabled',
                display: 'block',
                mb: 0.5,
                px: 0.5,
              }}
            >
              Table Fields
            </Typography>
            {tableFields.map((t) => {
              return (
                <Accordion
                  key={t.id}
                  disableGutters
                  elevation={0}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: '6px !important',
                    mb: 0.5,
                    '&:before': { display: 'none' },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ fontSize: 16 }} />}
                    sx={{ minHeight: 32, '& .MuiAccordionSummary-content': { my: 0.5 } }}
                  >
                    <Typography variant="caption" fontWeight={600} sx={{ color: 'text.primary' }}>
                      {t.field_template_name}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0, pb: 1, px: 1 }}>
                    {(t.subfields || []).length === 0 ? (
                      <Typography variant="caption" color="text.disabled">
                        No subfields
                      </Typography>
                    ) : (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {(t.subfields || []).map((sf) => (
                          <Box
                            key={sf.id}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                color: 'text.secondary',
                                maxWidth: '55%',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {sf.field_template_name}
                            </Typography>
                            <PlaceholderChip tag={`[[f_${sf.id}]]`} />
                          </Box>
                        ))}
                      </Box>
                    )}
                  </AccordionDetails>
                </Accordion>
              )
            })}
          </Box>
        )}
      </Box>
    </Box>
  )
}

// ─── Component ────────────────────────────────────────────────────────────────

const DocumentTemplateFormModal = ({
  open,
  onClose,
  modalData,
  setModalData,
  allActivities,
  allTables,
  activityFields = [],
  allRoutes, // null = still loading, [] | [...] = loaded
  approvalGridActivities,
  buttonLoading,
  onSave,
}) => {
  const [activeTab, setActiveTab] = useState(0)
  const [showHtmlPreview, setShowHtmlPreview] = useState(false)
  const [openDeleteGrouping, setOpenDeleteGrouping] = useState(false)
  const [deleteGroupingDetails, setDeleteGroupingDetails] = useState({ name: '', index: null })

  const yesOrNoOptions = [
    { value: true, label: 'Yes' },
    { value: false, label: 'No' },
  ]

  const isEditing = !!modalData?.id
  const routesLoading = allRoutes === null

  // ── Change handler ────────────────────────────────────────────────────────
  const handleChange = (type, value, index = null, subType = null) => {
    switch (type) {
      case 'grouping_table_config': {
        if (index >= 0 && !!subType) {
          const config = [...modalData.grouping_table_config]
          if (subType === 'table_field_template_id') {
            config[index]['table_field_template_id'] = value
            config[index]['grouping_headers'] = []
          } else if (subType === 'grouping_headers_subfields') {
            const existingHeaders = config[index]['grouping_headers'] || []
            config[index]['grouping_headers'] = value.map((fieldId) => {
              const existing = existingHeaders.find((h) => h.field_id === fieldId)
              if (existing) {
                return existing
              }
              return {
                field_id: fieldId,
                header: {
                  display_pattern: `[[f_${fieldId}]]`,
                  bg_color: '#f8fafc',
                },
                total: {
                  visible: true,
                  bg_color: '#f1f5f9',
                  display_pattern: `Total [[f_${fieldId}]]`,
                  subtotal_fields: [],
                },
              }
            })
          } else if (subType === 'grouping_header_level_update') {
            const { levelIndex, fieldPath, val } = value
            const headers = [...(config[index]['grouping_headers'] || [])]
            if (headers[levelIndex]) {
              const pathParts = fieldPath.split('.')
              let targetObj = headers[levelIndex]
              for (let i = 0; i < pathParts.length - 1; i++) {
                if (!targetObj[pathParts[i]]) {
                  targetObj[pathParts[i]] = {}
                }
                targetObj = targetObj[pathParts[i]]
              }
              targetObj[pathParts[pathParts.length - 1]] = val
            }
            config[index]['grouping_headers'] = headers
          } else {
            config[index][subType] = value
          }
          setModalData({ ...modalData, [type]: config })
        }
        break
      }
      default:
        setModalData({ ...modalData, [type]: value })
    }
  }

  // ── Grouping helpers ──────────────────────────────────────────────────────
  const getSubfieldsForGrouping = useCallback(
    (type, tableId) => {
      const tableData = allTables.find((it) => it.id == tableId)
      if (!tableData) return []
      switch (type) {
        case 'grouping_fields':
          return tableData.subfields?.filter((sf) => sf.data_structure === FIELD_TYPES.LIST) || []
        case 'subtotal_fields':
          return (
            tableData.subfields?.filter((sf) =>
              [FIELD_TYPES.NUMBER, FIELD_TYPES.CURRENCY].includes(sf.data_structure),
            ) || []
          )
        default:
          return []
      }
    },
    [allTables],
  )

  const handleAddGroupingTable = () => {
    const newRow = {
      table_field_template_id: null,
      grouping_mode: 'row',
      grouping_headers: [],
      show_heading_per_group: false,
    }
    setModalData({
      ...modalData,
      grouping_table_config: [...(modalData.grouping_table_config || []), newRow],
    })
  }

  const handleDeleteGroupingConfig = () => {
    const updated = modalData.grouping_table_config.filter(
      (_, i) => i !== deleteGroupingDetails.index,
    )
    setModalData({ ...modalData, grouping_table_config: updated })
    setOpenDeleteGrouping(false)
    setDeleteGroupingDetails({ name: '', index: null })
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="lg"
        PaperProps={{ sx: { height: '88vh', display: 'flex', flexDirection: 'column' } }}
      >
        {/* Header */}
        <DialogTitle
          sx={{
            px: 3,
            py: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight={600} component="div">
              {isEditing ? 'Edit Template' : 'Create Template'}
            </Typography>
            {isEditing && (
              <Typography variant="caption" color="text.secondary" component="div">
                #{modalData.id} · {modalData.document_template_name || 'Untitled'}
              </Typography>
            )}
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        {/* Tabs bar */}
        <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', px: 3, flexShrink: 0 }}>
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            textColor="secondary"
            indicatorColor="secondary"
          >
            <Tab label="Basic Info" sx={{ textTransform: 'none' }} />
            <Tab label="HTML Template" sx={{ textTransform: 'none' }} />
            <Tab label="Table Grouping" sx={{ textTransform: 'none' }} />
          </Tabs>
        </Box>

        {/* Scrollable body */}
        <DialogContent sx={{ flex: 1, overflowY: 'auto', px: 3, py: 0 }}>
          {/* ── Tab 0: Basic Info ── */}
          <TabPanel value={activeTab} index={0}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Box>
                <SectionLabel>Template identity</SectionLabel>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    size="small"
                    label="Document template name *"
                    value={modalData.document_template_name || ''}
                    onChange={(e) => handleChange('document_template_name', e.target.value)}
                    color="secondary"
                    fullWidth
                  />
                  <Autocomplete
                    size="small"
                    options={allActivities}
                    getOptionLabel={(option) =>
                      `${option.process_template_name} / ${option.activity_template_name}`
                    }
                    value={
                      allActivities.find(
                        (a) => a.activity_template_id === modalData?.activity_template_id,
                      ) || null
                    }
                    isOptionEqualToValue={(o, v) =>
                      o.activity_template_id == v?.activity_template_id
                    }
                    onChange={(_, v) =>
                      handleChange('activity_template_id', v?.activity_template_id)
                    }
                    renderInput={(params) => (
                      <TextField color="secondary" {...params} label="Select activity *" />
                    )}
                  />
                </Box>
              </Box>

              <Divider />

              <Box>
                <SectionLabel>Document settings</SectionLabel>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <TextField
                    size="small"
                    label="Sequence"
                    value={modalData.sequence ?? 1}
                    onChange={(e) => handleChange('sequence', e.target.value)}
                    type="number"
                    color="secondary"
                    sx={{ width: 120 }}
                  />
                  <Autocomplete
                    size="small"
                    options={yesOrNoOptions}
                    getOptionLabel={(o) => o.label}
                    value={yesOrNoOptions.find((b) => b.value === modalData?.is_landscape) || null}
                    isOptionEqualToValue={(o, v) => o?.value == v?.value}
                    onChange={(_, v) => handleChange('is_landscape', v?.value)}
                    sx={{ flex: 1, minWidth: 160 }}
                    renderInput={(params) => (
                      <TextField color="secondary" {...params} label="Is Landscape" />
                    )}
                  />
                  <Autocomplete
                    size="small"
                    options={yesOrNoOptions}
                    getOptionLabel={(o) => o.label}
                    value={
                      yesOrNoOptions.find(
                        (b) => b.value === modalData?.is_draft_until_end_accepted,
                      ) || null
                    }
                    isOptionEqualToValue={(o, v) => o.value == v?.value}
                    onChange={(_, v) => handleChange('is_draft_until_end_accepted', v?.value)}
                    sx={{ flex: 1, minWidth: 200 }}
                    renderInput={(params) => (
                      <TextField color="secondary" {...params} label="Is draft until end accept" />
                    )}
                  />
                </Box>
              </Box>

              <Divider />

              <Box>
                <SectionLabel>Trigger &amp; routing</SectionLabel>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {/*
                    Trigger routes fix: allRoutes starts as null (loading) in the parent.
                    We defer rendering the Autocomplete until the array is ready so the
                    value filter runs against the actual data, not an empty array.
                  */}
                  {routesLoading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
                      <CircularProgress size={16} color="secondary" />
                      <Typography variant="caption" color="text.secondary">
                        Loading routes…
                      </Typography>
                    </Box>
                  ) : (
                    <Autocomplete
                      size="small"
                      multiple
                      options={allRoutes}
                      getOptionLabel={(o) => o?.route_template_name || ''}
                      value={allRoutes.filter((r) =>
                        modalData?.trigger_route_template_ids?.includes(r.route_template_id),
                      )}
                      isOptionEqualToValue={(o, v) => o?.route_template_id === v?.route_template_id}
                      onChange={(_, v) =>
                        handleChange(
                          'trigger_route_template_ids',
                          v?.map((it) => it.route_template_id),
                        )
                      }
                      renderInput={(params) => (
                        <TextField color="secondary" {...params} label="Trigger routes" />
                      )}
                    />
                  )}
                  <Autocomplete
                    size="small"
                    multiple
                    options={approvalGridActivities || []}
                    getOptionLabel={(o) => o?.activity_template_name || ''}
                    value={
                      approvalGridActivities
                        ? approvalGridActivities.filter((a) =>
                            modalData?.activities_for_approval_grid?.includes(
                              a.activity_template_id,
                            ),
                          )
                        : []
                    }
                    isOptionEqualToValue={(o, v) =>
                      o?.activity_template_id === v?.activity_template_id
                    }
                    onChange={(_, v) =>
                      handleChange(
                        'activities_for_approval_grid',
                        v?.map((it) => it.activity_template_id),
                      )
                    }
                    renderInput={(params) => (
                      <TextField color="secondary" {...params} label="Approval grid activities" />
                    )}
                  />
                </Box>
              </Box>
            </Box>
          </TabPanel>

          {/* ── Tab 1: HTML Template ── */}
          <TabPanel value={activeTab} index={1}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
              {/* Editor column */}
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                <Box
                  sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <SectionLabel>HTML content</SectionLabel>
                  <Tooltip title="Preview rendered HTML">
                    <Button
                      size="small"
                      variant="outlined"
                      color="secondary"
                      startIcon={<VisibilityIcon fontSize="small" />}
                      onClick={() => setShowHtmlPreview(true)}
                      sx={{ textTransform: 'none' }}
                    >
                      Preview
                    </Button>
                  </Tooltip>
                </Box>
                <TextField
                  label="HTML template *"
                  multiline
                  fullWidth
                  minRows={22}
                  value={modalData.html_template || ''}
                  onChange={(e) => handleChange('html_template', e.target.value)}
                  color="secondary"
                  placeholder="Paste your HTML template here..."
                  helperText="Use [[f_id]] placeholders to insert field values. For table rows, place them inside <tr> tags. Use [[f_id.description]] for list metadata."
                  InputProps={{
                    sx: { fontFamily: 'monospace', fontSize: '0.82rem', lineHeight: 1.6 },
                  }}
                />
              </Box>

              {/* Placeholder sidebar */}
              <Box
                sx={{ width: 300, flexShrink: 0, top: 0, display: 'flex', flexDirection: 'column' }}
              >
                <PlaceholderSidebar activityFields={activityFields} allTables={allTables} />
              </Box>
            </Box>
          </TabPanel>

          {/* ── Tab 2: Table Grouping ── */}
          <TabPanel value={activeTab} index={2}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Section 1: Repeater (document split) */}
              <Box
                sx={{
                  p: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1.5,
                  bgcolor: 'background.default',
                }}
              >
                <SectionLabel>Document split / repeater (optional)</SectionLabel>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mb: 1.5 }}
                >
                  When set, the document will be split into one page per row of the selected table —
                  useful for generating per-item documents (e.g., one page per product).
                </Typography>
                <Autocomplete
                  size="small"
                  options={allTables}
                  getOptionLabel={(o) => o.field_template_name}
                  value={
                    allTables.find((t) => t.id === modalData?.repeater_table_field_template_id) ||
                    null
                  }
                  isOptionEqualToValue={(o, v) => o?.id == v?.id}
                  onChange={(_, v) => handleChange('repeater_table_field_template_id', v?.id)}
                  renderInput={(params) => (
                    <TextField color="secondary" {...params} label="Repeater table field" />
                  )}
                />
              </Box>

              <Divider />

              {/* Section 2: Table row grouping */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <SectionLabel>Table row grouping configurations</SectionLabel>
                  <Typography variant="caption" color="text.secondary">
                    Group table rows hierarchically and optionally show subtotals per group.
                  </Typography>
                </Box>
                <Button
                  size="small"
                  variant="outlined"
                  color="secondary"
                  startIcon={<AddIcon />}
                  onClick={handleAddGroupingTable}
                  sx={{ textTransform: 'none', flexShrink: 0 }}
                >
                  Add grouping
                </Button>
              </Box>

              {!modalData.grouping_table_config?.length ? (
                <Box
                  sx={{
                    border: '1.5px dashed',
                    borderColor: 'divider',
                    borderRadius: 2,
                    py: 5,
                    textAlign: 'center',
                    color: 'text.disabled',
                  }}
                >
                  <Typography variant="body2">No grouping configs yet.</Typography>
                  <Typography variant="caption">Click "Add grouping" to get started.</Typography>
                </Box>
              ) : (
                modalData.grouping_table_config.map((config, index) => (
                  <Box
                    key={index}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      p: 2,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2,
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Chip
                        label={`Config #${index + 1}`}
                        size="small"
                        variant="outlined"
                        sx={{ fontWeight: 600 }}
                      />
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => {
                          setOpenDeleteGrouping(true)
                          setDeleteGroupingDetails({
                            name:
                              allTables.find((it) => it.id == config.table_field_template_id)
                                ?.field_template_name || '',
                            index,
                          })
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Autocomplete
                        size="small"
                        sx={{ flex: 1 }}
                        options={allTables || []}
                        getOptionLabel={(o) => o.field_template_name}
                        value={
                          allTables.find((t) => t.id === config.table_field_template_id) || null
                        }
                        isOptionEqualToValue={(o, v) => o?.id == v?.id}
                        onChange={(_, v) =>
                          handleChange(
                            'grouping_table_config',
                            v?.id,
                            index,
                            'table_field_template_id',
                          )
                        }
                        renderInput={(params) => (
                          <TextField color="secondary" {...params} label="Grouping table field" />
                        )}
                      />
                      <TextField
                        select
                        size="small"
                        label="Mode"
                        value={config.grouping_mode || 'row'}
                        onChange={(e) =>
                          handleChange(
                            'grouping_table_config',
                            e.target.value,
                            index,
                            'grouping_mode',
                          )
                        }
                        color="secondary"
                        sx={{ width: 180 }}
                      >
                        <MenuItem value="row">Row Merge</MenuItem>
                        <MenuItem value="column_merge">Column Merge</MenuItem>
                      </TextField>
                      {config.grouping_mode !== 'column_merge' && (
                        <TextField
                          select
                          size="small"
                          label="Header Mode"
                          value={config.show_heading_per_group ? 'per_group' : 'table_top'}
                          onChange={(e) =>
                            handleChange(
                              'grouping_table_config',
                              e.target.value === 'per_group',
                              index,
                              'show_heading_per_group',
                            )
                          }
                          color="secondary"
                          sx={{ width: 180 }}
                        >
                          <MenuItem value="table_top">Show at Top</MenuItem>
                          <MenuItem value="per_group">Show per Group</MenuItem>
                        </TextField>
                      )}
                    </Box>

                    <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Typography variant="subtitle2" color="text.primary" fontWeight={600}>
                        Grouping Levels Configuration
                      </Typography>
                      <Autocomplete
                        size="small"
                        multiple
                        options={getSubfieldsForGrouping(
                          'grouping_fields',
                          config.table_field_template_id,
                        )}
                        getOptionLabel={(o) => o.field_template_name}
                        value={
                          getSubfieldsForGrouping(
                            'grouping_fields',
                            config.table_field_template_id,
                          )?.filter((it) =>
                            (config?.grouping_headers || []).map((h) => h.field_id).includes(it.id),
                          ) || []
                        }
                        isOptionEqualToValue={(o, v) => o?.id == v?.id}
                        onChange={(_, v) =>
                          handleChange(
                            'grouping_table_config',
                            v?.map((it) => it.id),
                            index,
                            'grouping_headers_subfields',
                          )
                        }
                        renderInput={(params) => (
                          <TextField
                            color="secondary"
                            {...params}
                            label="Select grouping levels (hierarchical order)"
                          />
                        )}
                      />

                      {(config?.grouping_headers || []).map((headerConfig, levelIndex) => {
                        const fieldId = headerConfig.field_id
                        const sfDetails = allTables
                          .find((t) => t.id === config.table_field_template_id)
                          ?.subfields?.find((s) => s.id === fieldId)
                        const levelName = sfDetails?.field_template_name || `Field #${fieldId}`

                        const headerSettings = headerConfig.header || {}
                        const totalSettings = headerConfig.total || {}

                        return (
                          <Card
                            key={fieldId}
                            variant="outlined"
                            sx={{
                              p: 2,
                              borderColor: 'divider',
                              bgcolor: 'background.default',
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 2,
                            }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                              }}
                            >
                              <Typography variant="body2" fontWeight={600} color="secondary.main">
                                Level {levelIndex + 1}: {levelName}
                              </Typography>
                              {/* Placeholder for current grouping field */}
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                <PlaceholderChip tag={`[[f_${fieldId}]]`} label={levelName} />
                              </Box>
                            </Box>

                            <Divider />

                            {/* Header Row Settings */}
                            <Box>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                fontWeight={600}
                                sx={{
                                  display: 'block',
                                  mb: 1,
                                  textTransform: 'uppercase',
                                  letterSpacing: 0.8,
                                }}
                              >
                                Header Row Configuration
                              </Typography>
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <TextField
                                  size="small"
                                  label="Header Pattern"
                                  value={headerSettings.display_pattern || ''}
                                  onChange={(e) =>
                                    handleChange(
                                      'grouping_table_config',
                                      {
                                        levelIndex,
                                        fieldPath: 'header.display_pattern',
                                        val: e.target.value,
                                      },
                                      index,
                                      'grouping_header_level_update',
                                    )
                                  }
                                  color="secondary"
                                  placeholder={`e.g. <strong>[[f_${fieldId}]]:</strong>`}
                                  fullWidth
                                  helperText="Pattern for the level header row. Use [[f_id]] for the grouped value or [[f_id.description]] for list metadata."
                                />

                                <Box
                                  sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 1,
                                    width: '100%',
                                  }}
                                >
                                  <TextField
                                    size="small"
                                    label="BG Color"
                                    value={headerSettings.bg_color || ''}
                                    onChange={(e) =>
                                      handleChange(
                                        'grouping_table_config',
                                        {
                                          levelIndex,
                                          fieldPath: 'header.bg_color',
                                          val: e.target.value,
                                        },
                                        index,
                                        'grouping_header_level_update',
                                      )
                                    }
                                    color="secondary"
                                    placeholder="e.g. #f8fafc or transparent"
                                    fullWidth
                                    InputProps={{
                                      endAdornment: (
                                        <InputAdornment position="end" sx={{ p: 0, m: 0 }}>
                                          <input
                                            type="color"
                                            value={
                                              headerSettings.bg_color &&
                                              /^#[0-9A-F]{6}$/i.test(headerSettings.bg_color)
                                                ? headerSettings.bg_color
                                                : '#ffffff'
                                            }
                                            onChange={(e) =>
                                              handleChange(
                                                'grouping_table_config',
                                                {
                                                  levelIndex,
                                                  fieldPath: 'header.bg_color',
                                                  val: e.target.value,
                                                },
                                                index,
                                                'grouping_header_level_update',
                                              )
                                            }
                                            style={{
                                              width: 28,
                                              height: 28,
                                              border: '1px solid #ccc',
                                              borderRadius: '4px',
                                              cursor: 'pointer',
                                              padding: 0,
                                              backgroundColor: 'transparent',
                                            }}
                                          />
                                        </InputAdornment>
                                      ),
                                    }}
                                  />
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      gap: 0.75,
                                      alignItems: 'center',
                                      flexWrap: 'wrap',
                                    }}
                                  >
                                    <Typography variant="caption" color="text.secondary">
                                      Presets:
                                    </Typography>
                                    {PRESET_COLORS.map((colorObj) => (
                                      <Tooltip key={colorObj.value} title={colorObj.label} arrow>
                                        <Box
                                          onClick={() => {
                                            handleChange(
                                              'grouping_table_config',
                                              {
                                                levelIndex,
                                                fieldPath: 'header.bg_color',
                                                val: colorObj.value,
                                              },
                                              index,
                                              'grouping_header_level_update',
                                            )
                                          }}
                                          sx={{
                                            width: 18,
                                            height: 18,
                                            borderRadius: '50%',
                                            bgcolor:
                                              colorObj.value === 'transparent'
                                                ? 'transparent'
                                                : colorObj.value,
                                            border: '1px solid',
                                            borderColor:
                                              (headerSettings.bg_color || '').toLowerCase() ===
                                              colorObj.value.toLowerCase()
                                                ? 'secondary.main'
                                                : '#ccc',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            boxShadow:
                                              (headerSettings.bg_color || '').toLowerCase() ===
                                              colorObj.value.toLowerCase()
                                                ? 2
                                                : 0,
                                            '&:hover': {
                                              borderColor: 'secondary.main',
                                              transform: 'scale(1.1)',
                                            },
                                            transition: 'transform 0.1s ease',
                                            position: 'relative',
                                            ...(colorObj.value === 'transparent' && {
                                              background:
                                                'linear-gradient(45deg, transparent 45%, #ff4d4f 45%, #ff4d4f 55%, transparent 55%)',
                                              borderStyle: 'dashed',
                                            }),
                                          }}
                                        />
                                      </Tooltip>
                                    ))}
                                  </Box>
                                </Box>
                              </Box>
                            </Box>

                            <Divider />

                            {/* Total Row Settings */}
                            <Box>
                              <Box
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  mb: 1.5,
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  fontWeight={600}
                                  sx={{ textTransform: 'uppercase', letterSpacing: 0.8 }}
                                >
                                  Total Row Configuration
                                </Typography>
                                <TextField
                                  select
                                  size="small"
                                  label="Show Subtotal"
                                  value={totalSettings.visible !== false ? 'true' : 'false'}
                                  onChange={(e) =>
                                    handleChange(
                                      'grouping_table_config',
                                      {
                                        levelIndex,
                                        fieldPath: 'total.visible',
                                        val: e.target.value === 'true',
                                      },
                                      index,
                                      'grouping_header_level_update',
                                    )
                                  }
                                  color="secondary"
                                  sx={{ width: 140 }}
                                >
                                  <MenuItem value="true">Enabled</MenuItem>
                                  <MenuItem value="false">Disabled</MenuItem>
                                </TextField>
                              </Box>

                              {totalSettings.visible !== false && (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                  <TextField
                                    size="small"
                                    label="Total Label Pattern"
                                    value={totalSettings.display_pattern || ''}
                                    onChange={(e) =>
                                      handleChange(
                                        'grouping_table_config',
                                        {
                                          levelIndex,
                                          fieldPath: 'total.display_pattern',
                                          val: e.target.value,
                                        },
                                        index,
                                        'grouping_header_level_update',
                                      )
                                    }
                                    color="secondary"
                                    placeholder={`e.g. Total [[f_${fieldId}]]`}
                                    fullWidth
                                    helperText={`Pattern for the level subtotal row label. Leave empty for default. Use [[f_id]] for the grouped value name.`}
                                  />

                                  <Box
                                    sx={{
                                      display: 'flex',
                                      flexDirection: 'column',
                                      gap: 1,
                                      width: '100%',
                                    }}
                                  >
                                    <TextField
                                      size="small"
                                      label="BG Color"
                                      value={totalSettings.bg_color || ''}
                                      onChange={(e) =>
                                        handleChange(
                                          'grouping_table_config',
                                          {
                                            levelIndex,
                                            fieldPath: 'total.bg_color',
                                            val: e.target.value,
                                          },
                                          index,
                                          'grouping_header_level_update',
                                        )
                                      }
                                      color="secondary"
                                      placeholder="e.g. #f1f5f9 or transparent"
                                      fullWidth
                                      InputProps={{
                                        endAdornment: (
                                          <InputAdornment position="end" sx={{ p: 0, m: 0 }}>
                                            <input
                                              type="color"
                                              value={
                                                totalSettings.bg_color &&
                                                /^#[0-9A-F]{6}$/i.test(totalSettings.bg_color)
                                                  ? totalSettings.bg_color
                                                  : '#ffffff'
                                              }
                                              onChange={(e) =>
                                                handleChange(
                                                  'grouping_table_config',
                                                  {
                                                    levelIndex,
                                                    fieldPath: 'total.bg_color',
                                                    val: e.target.value,
                                                  },
                                                  index,
                                                  'grouping_header_level_update',
                                                )
                                              }
                                              style={{
                                                width: 28,
                                                height: 28,
                                                border: '1px solid #ccc',
                                                borderRadius: '4px',
                                                cursor: 'pointer',
                                                padding: 0,
                                                backgroundColor: 'transparent',
                                              }}
                                            />
                                          </InputAdornment>
                                        ),
                                      }}
                                    />
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        gap: 0.75,
                                        alignItems: 'center',
                                        flexWrap: 'wrap',
                                      }}
                                    >
                                      <Typography variant="caption" color="text.secondary">
                                        Presets:
                                      </Typography>
                                      {PRESET_COLORS.map((colorObj) => (
                                        <Tooltip key={colorObj.value} title={colorObj.label} arrow>
                                          <Box
                                            onClick={() => {
                                              handleChange(
                                                'grouping_table_config',
                                                {
                                                  levelIndex,
                                                  fieldPath: 'total.bg_color',
                                                  val: colorObj.value,
                                                },
                                                index,
                                                'grouping_header_level_update',
                                              )
                                            }}
                                            sx={{
                                              width: 18,
                                              height: 18,
                                              borderRadius: '50%',
                                              bgcolor:
                                                colorObj.value === 'transparent'
                                                  ? 'transparent'
                                                  : colorObj.value,
                                              border: '1px solid',
                                              borderColor:
                                                (totalSettings.bg_color || '').toLowerCase() ===
                                                colorObj.value.toLowerCase()
                                                  ? 'secondary.main'
                                                  : '#ccc',
                                              cursor: 'pointer',
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'center',
                                              boxShadow:
                                                (totalSettings.bg_color || '').toLowerCase() ===
                                                colorObj.value.toLowerCase()
                                                  ? 2
                                                  : 0,
                                              '&:hover': {
                                                borderColor: 'secondary.main',
                                                transform: 'scale(1.1)',
                                              },
                                              transition: 'transform 0.1s ease',
                                              position: 'relative',
                                              ...(colorObj.value === 'transparent' && {
                                                background:
                                                  'linear-gradient(45deg, transparent 45%, #ff4d4f 45%, #ff4d4f 55%, transparent 55%)',
                                                borderStyle: 'dashed',
                                              }),
                                            }}
                                          />
                                        </Tooltip>
                                      ))}
                                    </Box>
                                  </Box>
                                  <Autocomplete
                                    size="small"
                                    multiple
                                    options={getSubfieldsForGrouping(
                                      'subtotal_fields',
                                      config.table_field_template_id,
                                    )}
                                    getOptionLabel={(o) => o.field_template_name}
                                    value={
                                      getSubfieldsForGrouping(
                                        'subtotal_fields',
                                        config.table_field_template_id,
                                      )?.filter((it) =>
                                        (totalSettings.subtotal_fields || []).includes(it.id),
                                      ) || []
                                    }
                                    isOptionEqualToValue={(o, v) => o?.id == v?.id}
                                    onChange={(_, v) =>
                                      handleChange(
                                        'grouping_table_config',
                                        {
                                          levelIndex,
                                          fieldPath: 'total.subtotal_fields',
                                          val: v?.map((it) => it.id) || [],
                                        },
                                        index,
                                        'grouping_header_level_update',
                                      )
                                    }
                                    renderInput={(params) => (
                                      <TextField
                                        color="secondary"
                                        {...params}
                                        label="Subtotal fields (columns to sum)"
                                      />
                                    )}
                                  />
                                </Box>
                              )}
                            </Box>
                          </Card>
                        )
                      })}
                    </Box>
                  </Box>
                ))
              )}
            </Box>
          </TabPanel>
        </DialogContent>

        {/* Footer */}
        <DialogActions
          sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider', gap: 1 }}
        >
          <Button
            variant="outlined"
            color="inherit"
            onClick={onClose}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="secondary"
            disabled={buttonLoading}
            onClick={onSave}
            sx={{ textTransform: 'none', minWidth: 100 }}
            startIcon={buttonLoading ? <CircularProgress size={14} color="inherit" /> : null}
          >
            {buttonLoading ? 'Saving…' : isEditing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* HTML Preview dialog */}
      <Dialog
        open={showHtmlPreview}
        fullWidth
        maxWidth="lg"
        onClose={() => setShowHtmlPreview(false)}
      >
        <DialogTitle>HTML Template Preview</DialogTitle>
        <DialogContent>
          <div
            dangerouslySetInnerHTML={{
              __html:
                modalData.html_template?.trim()?.length > 0
                  ? sanitizeHtml(modalData.html_template)
                  : '<p style="color:#888">No preview available.</p>',
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="info" onClick={() => setShowHtmlPreview(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete grouping confirmation */}
      <ConfirmationBox
        isDialogOpen={openDeleteGrouping}
        setIsDialogOpen={setOpenDeleteGrouping}
        title={`Delete ${deleteGroupingDetails?.name ? `"${deleteGroupingDetails.name}"` : 'this grouping config'}?`}
        onYes={handleDeleteGroupingConfig}
      />
    </>
  )
}

export default DocumentTemplateFormModal
